/**
 * MindCore SwarmClient SDK
 * Multi-agent swarm platform SDK
 */
/**
 * SwarmSession for managing a swarm connection
 */
export class SwarmSession {
    eventHandlers = new Map();
    ws = null;
    wsReconnectTimer = null;
    isClosed = false;
    swarmId;
    client;
    constructor(swarmId, client) {
        this.swarmId = swarmId;
        this.client = client;
    }
    getSwarmId() {
        return this.swarmId;
    }
    async connect() {
        const wsUrl = `${this.client.baseUrl.replace(/^http/, 'ws')}/swarm/${this.swarmId}/ws`;
        return new Promise((resolve, reject) => {
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = () => {
                console.log('[SwarmSession] WebSocket connected');
                resolve();
            };
            this.ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.handleMessage(data);
                }
                catch (err) {
                    console.error('[SwarmSession] Failed to parse message:', err);
                }
            };
            this.ws.onclose = () => {
                if (!this.isClosed) {
                    this.scheduleReconnect();
                }
            };
            this.ws.onerror = (err) => {
                reject(err);
            };
        });
    }
    handleMessage(data) {
        const event = {
            type: data.type || 'unknown',
            timestamp: data.timestamp || Date.now(),
            payload: data.payload || data,
        };
        // Call specific handlers
        const handlers = this.eventHandlers.get(event.type) || [];
        handlers.forEach(cb => {
            try {
                cb(event);
            }
            catch (err) {
                console.error('[SwarmSession] Handler error:', err);
            }
        });
        // Call wildcard handlers
        const wildcardHandlers = this.eventHandlers.get('*') || [];
        wildcardHandlers.forEach(cb => {
            try {
                cb(event);
            }
            catch (err) {
                console.error('[SwarmSession] Wildcard handler error:', err);
            }
        });
    }
    scheduleReconnect() {
        if (this.wsReconnectTimer)
            return;
        this.wsReconnectTimer = setTimeout(() => {
            this.wsReconnectTimer = null;
            this.connect().catch(() => {
                // Will retry again
            });
        }, 5000);
    }
    on(event, callback) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event).push(callback);
        return () => {
            const handlers = this.eventHandlers.get(event) || [];
            const index = handlers.indexOf(callback);
            if (index > -1)
                handlers.splice(index, 1);
        };
    }
    async getStatus() {
        const response = await fetch(`${this.client.baseUrl}/api/swarm/${this.swarmId}/status`);
        if (!response.ok) {
            throw new Error(`Failed to get status: ${response.statusText}`);
        }
        return response.json();
    }
    async pause() {
        await this.sendControl('pause');
    }
    async resume() {
        await this.sendControl('resume');
    }
    async cancel() {
        await this.sendControl('cancel');
    }
    async sendControl(action) {
        const response = await fetch(`${this.client.baseUrl}/api/swarm/${this.swarmId}/${action}`, { method: 'POST' });
        if (!response.ok) {
            throw new Error(`Failed to ${action}: ${response.statusText}`);
        }
    }
    async submitConsensusVote(approved, reasoning) {
        const response = await fetch(`${this.client.baseUrl}/api/swarm/${this.swarmId}/consensus`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ approved, reasoning }),
        });
        if (!response.ok) {
            throw new Error(`Failed to submit vote: ${response.statusText}`);
        }
    }
    send(message) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
    }
    close() {
        this.isClosed = true;
        if (this.wsReconnectTimer) {
            clearTimeout(this.wsReconnectTimer);
            this.wsReconnectTimer = null;
        }
        this.ws?.close();
    }
}
export class SwarmClient {
    baseUrl;
    apiKey;
    constructor(options) {
        this.baseUrl = options.apiUrl;
        this.apiKey = options.apiKey;
    }
    /**
     * Create a new swarm for building
     */
    async build(prompt, config = {}) {
        const response = await fetch(`${this.baseUrl}/api/swarm/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(this.apiKey && { 'Authorization': `Bearer ${this.apiKey}` }),
            },
            body: JSON.stringify({
                prompt,
                config,
            }),
        });
        if (!response.ok) {
            throw new Error(`Failed to create swarm: ${response.statusText}`);
        }
        const { swarmId } = await response.json();
        const session = new SwarmSession(swarmId, this);
        await session.connect();
        return session;
    }
    /**
     * Connect to an existing swarm
     */
    async connect(swarmId) {
        const session = new SwarmSession(swarmId, this);
        await session.connect();
        return session;
    }
    /**
     * List recent swarms
     */
    async listSwarms(limit = 10) {
        const response = await fetch(`${this.baseUrl}/api/swarm/list?limit=${limit}`, {
            headers: this.apiKey ? { 'Authorization': `Bearer ${this.apiKey}` } : {},
        });
        if (!response.ok) {
            throw new Error(`Failed to list swarms: ${response.statusText}`);
        }
        return response.json();
    }
}
